/**
 * AtmosphereLayer — the "alive" ambient layer (VFX-evolution M3 → modular layer
 * model). A scene's visuals are an ordered EffectLayer[] (resolveLayers: the
 * per-level editor data when present, else the code preset for the level id).
 * mount() is a thin loop — build each layer via EFFECT_REGISTRY, collect the
 * handles; unmount() destroys them. Adding an effect kind is a new module under
 * effects/ that self-registers — no edits here.
 *
 * Placement: ambient ISprites live in PixiOverlayHost.worldContainer
 * (camera-tracked) at Z_LAYERS.ATMOSPHERE — above the Phaser tilemap background,
 * behind Pixi entities. Best-effort + budget-aware: a missing glow texture or a
 * not-ready renderer just yields a quieter scene, never a throw.
 *
 * Lifecycle: mount on LEVEL_LOADED (layers chosen by level id), unmount on
 * LEVEL_UNLOADED / level change.
 *
 * @see ./SPEC.md — language-neutral lift spec (config + algorithm + renderer seam).
 */
import { EventBus, EVENTS } from '../../../shared/EventBus';
import { gameLogger } from '../../../utils/GameLogger';
import { hasSharedTexture } from '../../rendering/PixiSprite';
import { glowTexturesReady } from '../../rendering/glowTextures';
import { PixiOverlayHost } from '../../rendering/PixiOverlayHost';
import { ServiceRegistry } from '../../core/ServiceRegistry';
import { presetForLevel } from '../../../config/atmosphere/presets.config';
import { Z_LAYERS } from '../../rendering/Z_LAYERS';
import { hashSeed, deriveLayerSeed, regionFromBounds, regionFromCamera, type Bounds, type Region } from './atmospherePlacement';
import { EFFECT_REGISTRY, type BuildCtx, type EffectHandle } from './effectBuilders';
import { resolveLayersForMount } from './resolveLayers';
import { resolveScene } from './scene/resolveScene';
import type { EffectLayer, EffectKind } from '../../../config/atmosphere/effectLayer.types';
import type { WorldLayerHandle } from '../../rendering/IRenderer';
import { modulatedValue, modulatedTintValue, type Modulator } from '../../../config/atmosphere/modulator';
import { getMotionPrefs } from '../../systems/accessibility/motionPrefs';
import { TIME_GRADE_BLEND, type TimeGrade, type TimeOfDayPhase } from '../../../config/atmosphere/timeOfDay.config';
import { dayResponseFor, dayOpacityMultiplier, daySpeedMultiplier, dayDensityMultiplier, dayGradeStrengthMultiplier } from '../../../config/atmosphere/timeOfDayResponse.config';
import { REACTIVITY, type ReactivityPulseKind, type ReactivityResponse } from '../../../config/atmosphere/reactivity.config';
import { ReactivityModel, type PulseVerdict } from '../../systems/atmosphere/ReactivityModel';
import './effects'; // side-effect: self-registers every effect into EFFECT_REGISTRY
import { bootSearchParams } from '../../../config/boot/bootUrl';

const DEFAULT_BOUNDS: Bounds = { width: 1600, height: 720 };

/** A built handle paired with the modulators driving it (per-track seed keeps
 *  same-shape modulators on different layers from peaking in lockstep).
 *  `container` carries the per-layer opacity; `baseOpacity` is the static
 *  layer.opacity; `mooded` gates scene-mood scaling (frame kinds opt out);
 *  `kind` feeds the per-layer day-response multiplier lookup. */
interface ModTrack { container: WorldLayerHandle | null; handle: EffectHandle; mods: Modulator[]; seed: number; baseOpacity: number; mooded: boolean; kind: EffectKind;
  /** Reactivity dirty-flag: true while a pulse factor last drove this track —
   *  buys ONE restoring write when the envelopes decay out, so the neutral-skip
   *  guard can't strand a pulse-elevated alpha. */
  pulseTouched?: boolean; }

/** The authored colorGrade layer captured at mount — the time-of-day grade
 *  blends toward this so the biome's own cinematic identity survives the day
 *  clock (night cools/darkens it, noon lets it read through). */
interface GradeTrack { handle: EffectHandle; top: number; bottom: number; strength: number; }

/** Linear channel lerp between two 0xRRGGBB colours (the time grade itself is
 *  already eased by the driver, so the per-tick blend is linear). */
function blendColor(a: number, b: number, t: number): number {
  const c = Math.max(0, Math.min(1, t));
  const ar = (a >> 16) & 0xff, ag = (a >> 8) & 0xff, ab = a & 0xff;
  const br = (b >> 16) & 0xff, bg = (b >> 8) & 0xff, bb = b & 0xff;
  const r = Math.round(ar + (br - ar) * c);
  const g = Math.round(ag + (bg - ag) * c);
  const bl = Math.round(ab + (bb - ab) * c);
  return (r << 16) | (g << 8) | bl;
}

class AtmosphereLayerImpl {
  private layers: Array<{ handle: EffectHandle; container: WorldLayerHandle | null }> = [];
  private currentPreset: string | null = null;
  private pendingLevelId: string | null = null;
  private installed = false;
  // ── Modulation (Scene Effects v2) ──
  private modTracks: ModTrack[] = [];
  private modUnsub: (() => void) | null = null;
  private modElapsedMs = 0;
  /** Scene-mood multiplier — game code dials ambient intensity (combat, night). */
  private sceneMood = 1;
  /** Combat-mood pulse multiplier (CombatMoodDriver via EVENTS.COMBAT_MOOD) —
   *  multiplied WITH sceneMood so combat heat intensifies the ambiance without
   *  overwriting the per-level base mood. Default 1 (no pulse). */
  private combatMood = 1;
  /** Boss-mood swell multiplier (BossMoodDriver via EVENTS.BOSS_MOOD) —
   *  multiplied WITH sceneMood × combatMood so boss phase transitions swell the
   *  ambiance (the undercroft dais intensifies as the Hollow Keeper escalates)
   *  and settle on defeat. Default 1 (no swell). Lane E S1. */
  private bossMood = 1;
  // ── Time-of-day phase 2 (TimeOfDayDriver via EVENTS.TIME_OF_DAY) ──
  /** The authored colorGrade track (captured at mount) — the time grade blends
   *  toward it. null when the scene has no colorGrade layer (rare) or after
   *  unmount. The driver does NOT emit for exempt scenes, so this stays authored
   *  there (the undercroft keeps its eternal dark grade). */
  private gradeTrack: GradeTrack | null = null;
  /** The current day-phase from the clock (null = no time signal yet → neutral
   *  per-layer response, authored grade). Set each tick by the driver. */
  private todPhase: TimeOfDayPhase | null = null;
  /** The current time-keyed grade from the clock (null = no time signal yet). */
  private todGrade: TimeGrade | null = null;
  /** Cached day-response knobs for the current preset (empty for exempt/unknown). */
  private dayKnobs = dayResponseFor('');
  // ── World-combat reactivity (fx-grammar spec) ──
  /** The fixed-pool envelope model — transient decay-to-1 factors folded into
   *  tickModulation. Kind-keyed (not handle-keyed), so live pulses simply keep
   *  decaying across camera-settle re-mounts — no re-mount interaction. */
  private reactivity = new ReactivityModel();
  /** The mounted scene's resolved biome (feeds the driver's family lookup). */
  private currentBiome: string | null = null;
  // ── Live-edit (Scene Effects v2 — the in-game tuner reads/replaces these) ──
  private currentLayers: EffectLayer[] = [];
  private currentBounds: Bounds = DEFAULT_BOUNDS;
  private currentLevelId: string | null = null;
  /** Monotonic mount counter — feeds `varyOnReload` placement (M2). */
  private mountCount = 0;
  // ── Camera-settle gate (BUG: atmosphere mounted before camera pans to player) ──
  // On LEVEL_LOADED the camera often still sits at the level's default origin
  // (e.g. 0,0 or the level center) — it only lerps to the player a few frames
  // later. The original mount captured that stale view via `getCameraView()`
  // and scattered every ambient sprite around the wrong region, leaving
  // leviathans + schooling motes + caustics far off-screen (the sunken_drift
  // "no whales" bug). The gate polls the live camera view for a short window
  // after mount; if it shifts beyond a threshold, it re-mounts once so the
  // region tracks where the player actually is.
  private settlePollUnsub: (() => void) | null = null;
  private settlePollStart = 0;
  private settleReMounted = false;
  /** How many times the settle poll has re-mounted for the current level.
   *  Capped at SETTLE_MAX_REMOUNTS to prevent infinite loops if the camera
   *  keeps drifting (the player walking). */
  private settleRemountCount = 0;
  private static readonly SETTLE_MAX_REMOUNTS = 1;
  /** Max ms to poll for camera settle before giving up. Generous because the
   *  camera may not start following the player until the player sprite spawns
   *  (which can lag LEVEL_LOADED by several seconds while the network/auth
   *  handshake completes), and the lerp itself takes 1-2s to converge. 15s
   *  covers the worst observed cold-load case. */
  private static readonly SETTLE_POLL_MS = 15000;
  /** Re-mount when the camera center moves more than this many px from the
   *  view captured at mount. Half a viewport-height is enough to signal the
   *  camera has begun tracking the player (not just noise). */
  private static readonly SETTLE_SHIFT_PX = 300;
  /** Per-frame camera delta (px) below which the camera is considered "stable"
   *  for that frame. The lerp convergence produces progressively smaller
   *  deltas; below this threshold the camera has effectively arrived. */
  private static readonly SETTLE_STABLE_PX = 2;
  /** Consecutive stable frames required before re-mounting. Filters out
   *  momentary stillness during a lerp (the camera can pause for a frame
   *  between steps). 30 frames ≈ 0.5s at 60fps. */
  private static readonly SETTLE_STABLE_FRAMES = 30;

  /** Wire the level-load lifecycle. Idempotent. */
  install(): void {
    if (this.installed) return;
    this.installed = true;
    // Debug surface for Visual QA probes — preset + counts of the live layer.
    try { (globalThis as { __atmosphere?: unknown }).__atmosphere = this; } catch { /* no global */ }
    const bus = EventBus.getInstance();
    bus.on(EVENTS.SCENE_MOOD, (d: { intensity?: number }) => this.setSceneMood(typeof d?.intensity === 'number' ? d.intensity : 1));
    bus.on(EVENTS.COMBAT_MOOD, (d: { intensity?: number }) => this.setCombatMood(typeof d?.intensity === 'number' ? d.intensity : 1));
    bus.on(EVENTS.BOSS_MOOD, (d: { intensity?: number }) => this.setBossMood(typeof d?.intensity === 'number' ? d.intensity : 1));
    // Time-of-day phase 2 — the driver pushes the eased day-phase + time-keyed
    // colorGrade palette each tick. Store both; the grade is applied immediately
    // (blended with the authored grade), the phase drives the per-layer
    // day-response multipliers in tickModulation. Exempt scenes never receive
    // this (the driver doesn't emit), so their authored grade + neutral response
    // are preserved.
    bus.on(EVENTS.TIME_OF_DAY, (d: { phase?: TimeOfDayPhase; grade?: TimeGrade }) => {
      this.todPhase = d?.phase ?? null;
      this.todGrade = d?.grade ?? null;
      this.applyTimeGrade();
    });
    bus.on(EVENTS.LEVEL_LOADED, (data: { id?: string; width?: number; height?: number }) => {
      const levelId = data?.id;
      if (!levelId) { this.unmount(); return; }
      // Resolve layers against the FULL current level (MapSystem set it before
      // emitting LEVEL_LOADED), so an authored `scene.biome` / `effectLayers`
      // binds — the event payload alone is id-only, which would silently drop
      // both (scene-schema cold-load gap). Falls back to id-routing if the level
      // isn't current yet.
      const currentLevel = ServiceRegistry.getInstance()
        .get('MapSystem', { optional: true })
        ?.getCurrentLevel?.();
      const layers = resolveLayersForMount(levelId, currentLevel);
      if (layers.length === 0) { this.unmount(); return; }
      const bounds = { width: data?.width || DEFAULT_BOUNDS.width, height: data?.height || DEFAULT_BOUNDS.height };
      // Wait for BOTH async readiness signals before mounting:
      //  • glowTexturesReady — glow sprites register at boot (the texture race).
      //  • PixiOverlayHost.ensureReady — the overlay's worldContainer must exist,
      //    else mount()'s createWorldLayerContainer throws → every layer falls back
      //    to a null container and scene-mood/per-layer-opacity reach NOTHING until
      //    a re-mount (the cold-load host-not-ready bug, M1 VQA 2026-06-18). Unlike
      //    sprite creation (which defers internally), container creation is
      //    throw-or-nothing, so the cold scene needs this gate to light up.
      void Promise.all([glowTexturesReady, PixiOverlayHost.ensureReady()])
        .then(() => { if (this.pendingLevelId === levelId) this.mount(layers, bounds, levelId); })
        .catch((e) => gameLogger.warn('vfx', `[Atmosphere] deferred mount skipped: ${(e as Error)?.message ?? e}`));
      this.pendingLevelId = levelId;
    });
    bus.on(EVENTS.LEVEL_UNLOADED, () => this.unmount());
  }

  /** Build a scene from its layer list. Replaces any current layer. */
  mount(layers: EffectLayer[], bounds: Bounds, levelId: string): void {
    // Capture whether this is a same-level re-mount (camera-settle) vs a real
    // level transition. The time-of-day signal (todPhase/todGrade) is retained
    // across same-level re-mounts (the driver keeps ticking; a re-mount would
    // otherwise drop the night grade when the driver's settle window has expired
    // + the grade is stable at a keyframe) but cleared on a level transition so
    // an exempt scene (coil_undercroft) doesn't inherit the previous outdoor
    // scene's grade.
    const prevLevelId = this.currentLevelId;
    const levelChanged = prevLevelId !== levelId;
    this.unmount();

    // Diagnostic toggle: ?noatmo=1 disables all atmosphere layers to test whether
    // heavy ambient effects (leviathans, schooling motes, caustics, fog, etc.)
    // are causing GPU/compositor stalls that show up as rAF gaps.
    if (this.readUrlParam('noatmo') === '1') {
      gameLogger.info('vfx', '[Atmosphere] ?noatmo=1 active — skipping mount');
      this.currentPreset = null;
      this.currentLayers = [];
      this.currentLevelId = levelId;
      return;
    }

    this.currentPreset = presetForLevel(levelId)?.id ?? levelId;
    this.currentLayers = layers;
    this.currentBounds = bounds;
    this.currentLevelId = levelId;
    // Phase 2 — cache the preset's day-response knobs + reset the grade track
    // (it's per-mount — the colorGrade layer is rebuilt). The time signal is
    // retained across same-level re-mounts but cleared on a level transition.
    this.dayKnobs = dayResponseFor(this.currentPreset);
    this.gradeTrack = null;
    if (levelChanged) {
      this.todPhase = null;
      this.todGrade = null;
    }
    this.mountCount += 1; // drives `varyOnReload` placement (deterministic per mount)
    try {
      // Resolve the scene from the live level (MapSystem.getCurrentLevel) — its
      // platforms/scene block drive terrain anchors + biome + mood. When no
      // MapSystem is registered (cold/headless mount), default to the level id +
      // bounds so the resolver still derives a sane scene and never throws.
      const lvl = ServiceRegistry.getInstance()
        .get('MapSystem', { optional: true })
        ?.getCurrentLevel?.();
      const scene = resolveScene(lvl ?? { id: levelId, height: bounds.height });
      // Reactivity: the resolved biome keys the family-scoped response lookup.
      this.currentBiome = scene.biome ?? null;
      // Seed the per-level mood once before the layer loop so it applies from the
      // first frame; the runtime EVENTS.SCENE_MOOD dial still overrides afterwards.
      // On a same-level re-mount (camera-settle), RETAIN the live mood — the
      // TimeOfDayDriver's diurnal factor (0.78 at night) would otherwise be
      // clobbered by the authored base (1.0) and the driver's settle window may
      // not outlast the re-mount.
      if (levelChanged) this.setSceneMood(scene.sceneMood);
      const seed = hashSeed(levelId);
      // Scatter around the CAMERA view (what the player sees) when available,
      // else the level box at world origin (level content is offset from origin,
      // so origin-relative scatter would land off-screen).
      const view = PixiOverlayHost.getCameraView();
      const region: Region = view ? regionFromCamera(view) : regionFromBounds(bounds);
      const ctx: BuildCtx = {
        region,
        bounds,
        seed,
        levelId,
        scene,
        addTick: (fn) => PixiOverlayHost.addTickListener(fn),
        getCameraView: () => PixiOverlayHost.getCameraView(),
        getScreenSize: () => PixiOverlayHost.getScreenSize(),
      };
      let trackSeed = seed;
      for (const layer of layers) {
        if (layer.enabled === false) continue;
        const desc = EFFECT_REGISTRY[layer.kind];
        if (!desc) continue;
        // Per-layer placement seed (M2 variation): stable by default; shifted by
        // seedOffset; re-randomized per mount when varyOnReload. ctx.seed feeds
        // every builder's placement, so a per-layer ctx is all it takes.
        const layerSeed = deriveLayerSeed(seed, layer, this.mountCount);
        // One container per layer at the layer's `depth` override (granular
        // multi-plane parallax, PX1), else the kind's descriptor depth (keeps
        // z-order). Fall back to null (→ worldContainer) if the host isn't ready.
        let container: WorldLayerHandle | null = null;
        try { container = PixiOverlayHost.createWorldLayerContainer(layer.depth ?? desc.depth ?? Z_LAYERS.ATMOSPHERE); }
        catch { container = null; }
        const layerCtx: BuildCtx = { ...ctx, seed: layerSeed, layerContainer: container ?? undefined };
        const handle = desc.build(layer, layerCtx);
        if (!handle) { container?.destroy(); continue; }
        this.layers.push({ handle, container });
        // Opacity is a container concern — apply the static layer.opacity once.
        const baseOpacity = layer.opacity ?? 1;
        container?.setAlpha(baseOpacity);
        // Phase 2 — capture the authored colorGrade layer so the time-of-day
        // grade can blend toward it. Only the FIRST colorGrade layer is tracked
        // (presets author exactly one via biomeCloser); `setGrade` is the live
        // update seam the driver pushes each tick.
        if (layer.kind === 'colorGrade' && handle.setGrade && !this.gradeTrack) {
          this.gradeTrack = { handle, top: layer.top, bottom: layer.bottom, strength: layer.strength };
        }
        // Track any layer with a container (opacity) OR a content setter — so both
        // the layer's own modulators AND the global scene-mood dial can reach it.
        // (mods may be empty: a mood-only track scales opacity/density by mood.)
        const mods = layer.modulators ?? [];
        if (container || handle.setDensity || handle.setSpeed) {
          this.modTracks.push({ container, handle, mods, seed: trackSeed, baseOpacity, mooded: desc.mooded ?? true, kind: layer.kind });
        }
        trackSeed += 97; // decorrelate per-layer noise/phase
      }
      this.startModulation();
      // Phase 2 — if a time-of-day signal is already live (the driver ticks
      // continuously across level loads), re-apply it to the freshly built
      // colorGrade so a camera-settle re-mount doesn't drop the night grade.
      this.applyTimeGrade();
      // Start the camera-settle gate. When the camera view was available at
      // mount, we poll for a significant shift (the camera panning to the
      // player after a late follow-start). When it was NOT available (camera
      // source not bound yet → region fell back to bounds), we poll for the
      // camera becoming available at all — the first non-null view triggers
      // an immediate re-mount so the region tracks the player from the start.
      this.startSettlePoll(view ?? null);
      gameLogger.info('vfx', `[Atmosphere] mounted '${this.currentPreset}' (${this.spriteCount()} sprites + ${this.softLightCount()}-light scene, ${this.modTracks.length} modulatable, region=${view ? 'camera' : 'bounds'}, glow_soft=${hasSharedTexture('glow_soft')}) for ${levelId}`);
    } catch (e) {
      gameLogger.warn('vfx', `[Atmosphere] mount failed: ${(e as Error)?.message ?? e}`);
    }
  }

  /** Begin polling the camera view for a short window after mount. The poll
   *  has two phases:
   *  1. DETECT: wait for the camera to become available (if null at mount)
   *     or to shift significantly from the mount position (the camera started
   *     panning to the player). This filters out the case where the camera is
   *     already at the player and never moves.
   *  2. STABILIZE: once detected, wait for the camera to stop moving (per-frame
   *     delta drops below SETTLE_STABLE_PX for SETTLE_STABLE_FRAMES consecutive
   *     frames). This ensures we re-mount when the camera has FINISHED panning,
   *     not mid-lerp (which would place sprites at an intermediate position). */
  private startSettlePoll(mountView: { centerX: number; centerY: number } | null): void {
    this.stopSettlePoll();
    this.settleReMounted = false;
    this.settlePollStart = performance.now();
    let detected = !mountView; // if no view at mount, any non-null view triggers detect
    let stableFrames = 0;
    let lastView: { centerX: number; centerY: number } | null = null;
    const check = () => {
      if (this.settleReMounted || !this.currentLevelId) return;
      if (this.settleRemountCount >= AtmosphereLayerImpl.SETTLE_MAX_REMOUNTS) { this.stopSettlePoll(); return; }
      const elapsed = performance.now() - this.settlePollStart;
      if (elapsed > AtmosphereLayerImpl.SETTLE_POLL_MS) { this.stopSettlePoll(); return; }
      const now = PixiOverlayHost.getCameraView();
      if (!now) return;
      // Phase 1: DETECT — has the camera become available or shifted?
      if (!detected) {
        const dx = now.centerX - mountView!.centerX;
        const dy = now.centerY - mountView!.centerY;
        if (Math.hypot(dx, dy) > AtmosphereLayerImpl.SETTLE_SHIFT_PX) {
          detected = true;
        }
      }
      // Phase 2: STABILIZE — wait for the camera to stop moving.
      if (detected) {
        if (lastView) {
          const ddx = now.centerX - lastView.centerX;
          const ddy = now.centerY - lastView.centerY;
          if (Math.hypot(ddx, ddy) < AtmosphereLayerImpl.SETTLE_STABLE_PX) {
            stableFrames += 1;
          } else {
            stableFrames = 0; // reset — camera still moving
          }
        }
        lastView = { centerX: now.centerX, centerY: now.centerY };
        if (stableFrames >= AtmosphereLayerImpl.SETTLE_STABLE_FRAMES) {
          this.settleReMounted = true;
          this.settleRemountCount += 1;
          this.stopSettlePoll();
          const layers = this.currentLayers;
          const bounds = this.currentBounds;
          const levelId = this.currentLevelId;
          gameLogger.info('vfx', `[Atmosphere] camera stabilized (center=${Math.round(now.centerX)},${Math.round(now.centerY)}) — re-mounting '${this.currentPreset}' so the ambient region tracks the player.`);
          this.mount(layers, bounds, levelId);
        }
      }
    };
    this.settlePollUnsub = PixiOverlayHost.addTickListener(check);
  }

  private stopSettlePoll(): void {
    try { this.settlePollUnsub?.(); } catch { /* gone */ }
    this.settlePollUnsub = null;
  }

  unmount(): void {
    this.stopSettlePoll();
    this.settleRemountCount = 0; // reset for the next level load
    this.stopModulation();
    this.modTracks = [];      // cleared HERE (not in stopModulation, which the
    this.modElapsedMs = 0;    // mount→startModulation path calls AFTER building tracks)
    // Phase 2 — drop the time-of-day grade track + cached day-response. The
    // time signal itself (todPhase/todGrade) is retained across same-level
    // re-mounts (mount() clears it only on a real level transition) so a
    // camera-settle re-mount doesn't drop the night grade.
    this.gradeTrack = null;
    this.dayKnobs = dayResponseFor('');
    // Destroy the content handle first, then its container (no double-free: the
    // handle owns its sprites; the container owns the display node).
    for (const { handle, container } of this.layers) {
      try { handle.destroy(); } catch { /* already gone */ }
      try { container?.destroy(); } catch { /* already gone */ }
    }
    this.layers = [];
    this.currentPreset = null;
    this.pendingLevelId = null;
    // Reactivity envelopes are NOT reset — they are kind-keyed transients that
    // decay to 1 on their own; a camera-settle re-mount keeps them decaying
    // against the fresh tracks. Only the biome key clears with the scene.
    this.currentBiome = null;
  }

  /** Game-facing scene-mood dial (also driven by EVENTS.SCENE_MOOD): a global
   *  multiplier on every modulatable layer's density + opacity. 1 = neutral,
   *  >1 = intensify (combat/event), <1 = subdue (night/calm). */
  setSceneMood(intensity: number): void {
    this.sceneMood = Math.max(0, intensity);
  }

  /** Current scene-mood multiplier (for the live tuner readout). */
  getSceneMood(): number {
    return this.sceneMood;
  }

  /** Combat-mood pulse multiplier (driven by EVENTS.COMBAT_MOOD / the
   *  CombatMoodDriver). Multiplied with sceneMood in tickModulation, so the
   *  scene intensifies on combat heat without clobbering the level's base mood. */
  setCombatMood(factor: number): void {
    this.combatMood = Math.max(0, factor);
  }

  /** Current combat-mood pulse multiplier (tuner readout). */
  getCombatMood(): number {
    return this.combatMood;
  }

  /** Boss-mood swell multiplier (driven by EVENTS.BOSS_MOOD / the
   *  BossMoodDriver). Multiplied with sceneMood × combatMood in
   *  tickModulation, so boss phase transitions swell the ambiance without
   *  clobbering the level's base mood or the cast-heat pulse. Lane E S1. */
  setBossMood(factor: number): void {
    this.bossMood = Math.max(0, factor);
  }

  /** Current boss-mood swell multiplier (tuner readout). */
  getBossMood(): number {
    return this.bossMood;
  }

  /** The mounted scene's resolved biome id (null when nothing is mounted) —
   *  the AtmosphereReactivityDriver keys its family lookup on this. */
  activeBiome(): string | null {
    return this.currentBiome;
  }

  /** World-combat reactivity seam (spec §3.3): push resolved response
   *  envelopes for a pulse. Fixed pool; transient; factors decay to 1 — never
   *  writes layers/presets (the tuner remains the sole owner of persistent
   *  values). Scalar responses land in the envelope pool (evaluated in
   *  tickModulation); accepted positional responses fan out immediately to
   *  matching tracks' `handle.onPulse` — one call per matching layer, at pulse
   *  time, not per frame. Merged (cooldown-window) pulses refresh the live
   *  envelopes and deliberately skip the positional fan-out (no machine-gun
   *  kicks). */
  pushPulse(pulse: { kind: ReactivityPulseKind; school?: string | null; x: number; y: number; hasPosition: boolean; intensity: number; responses: ReactivityResponse[] }): PulseVerdict {
    const now = this.nowMs();
    const verdict = this.reactivity.push(
      { kind: pulse.kind, school: pulse.school, intensity: pulse.intensity, hasPosition: pulse.hasPosition },
      pulse.responses,
      now,
    );
    if (verdict.accepted && !verdict.merged && verdict.positional.length > 0) {
      const intensity = Math.max(0, Math.min(1, pulse.intensity));
      for (const r of verdict.positional) {
        const radius = r.radius ?? REACTIVITY.defaultRadiusPx;
        const kick = intensity * r.amp;
        for (const tr of this.modTracks) {
          if (tr.kind !== r.kind || !tr.handle.onPulse) continue;
          try { tr.handle.onPulse(pulse.x, pulse.y, kick, radius); } catch { /* VFX is non-critical */ }
        }
      }
    }
    return verdict;
  }

  /** Debug/VQA readout — live reactivity envelopes with current factors
   *  (`__atmosphere.getActivePulses()`, spec §6 JSON observability). */
  getActivePulses(): Array<{ kind: string; school: string | null; layerKind: string; channel: string; factor: number }> {
    return this.reactivity.activePulses(this.nowMs());
  }

  /** Hard-reset the reactivity envelope pool + gates (full teardown / tests).
   *  NOT part of the mount/unmount path — live pulses deliberately survive
   *  camera-settle re-mounts (kind-keyed transients that decay on their own). */
  resetReactivity(): void {
    this.reactivity.reset();
  }

  /** Monotonic ms clock for the reactivity envelopes (test-mockable). */
  private nowMs(): number {
    return typeof performance !== 'undefined' ? performance.now() : Date.now();
  }

  /** Phase 2 — blend the authored colorGrade toward the current time-of-day
   *  grade + push it to the live colorGrade handle. No-op when there is no
   *  grade track (no colorGrade layer) or no time signal yet (the driver hasn't
   *  emitted, e.g. an exempt scene or before the first tick) → the authored
   *  grade stays untouched. The blend factor `TIME_GRADE_BLEND` preserves biome
   *  identity while letting night cool/darken + dawn/dusk warm the scene; the
   *  preset's day-response `colorGrade.strength` multiplier folds on top. */
  private applyTimeGrade(): void {
    const track = this.gradeTrack;
    if (!track || !this.todGrade) return; // authored grade stays
    const tg = this.todGrade;
    const phase = this.todPhase;
    const strengthMul = phase ? dayGradeStrengthMultiplier(this.dayKnobs, phase) : 1;
    const top = blendColor(track.top, tg.top, TIME_GRADE_BLEND);
    const bottom = blendColor(track.bottom, tg.bottom, TIME_GRADE_BLEND);
    const strength = Math.max(0, Math.min(1,
      (track.strength + (tg.strength - track.strength) * TIME_GRADE_BLEND) * strengthMul));
    track.handle.setGrade?.(top, bottom, strength);
  }

  /** A deep copy of the mounted scene's source layers — the in-game tuner edits
   *  this copy then calls applyLayers(). Plain data (no funcs), so JSON-clones. */
  getActiveLayers(): EffectLayer[] {
    try { return JSON.parse(JSON.stringify(this.currentLayers)); } catch { return []; }
  }

  /** Diagnostic snapshot for freeze attribution: which level, preset, and layer
   *  kinds are active right now. Safe to call any time; returns nulls when idle. */
  getDiagnosticsSnapshot(): { levelId: string | null; presetId: string | null; layerKinds: string[]; spriteCount: number; lightCount: number } {
    return {
      levelId: this.currentLevelId,
      presetId: this.currentPreset,
      layerKinds: this.currentLayers.map(l => l.kind),
      spriteCount: this.spriteCount(),
      lightCount: this.softLightCount(),
    };
  }

  /** Re-mount the active scene with an edited layer list (the tuner's live
   *  apply). No-op if nothing is currently mounted. */
  applyLayers(layers: EffectLayer[]): void {
    if (!this.currentLevelId) return;
    this.mount(layers, this.currentBounds, this.currentLevelId);
  }

  /** Start the per-frame modulation ticker (no-op when nothing is modulatable).
   *  Called AFTER mount() builds modTracks, so it must NOT clear them. */
  private startModulation(): void {
    this.stopModulation();
    this.modElapsedMs = 0;
    if (this.modTracks.length === 0) return;
    // Accessibility: when reduced-motion is on, do NOT attach the ticker — the
    // scene renders its static base opacity (set at mount) instead of the LFO
    // breathing / density / speed pulse. Re-evaluated each mount, so a Settings
    // change takes effect on the next level load. Effect-internal motion (weather,
    // flicker, fog, candles...) is held still by each effect's own builder.
    if (getMotionPrefs().reduceMotion) return;
    this.modUnsub = PixiOverlayHost.addTickListener((dt) => this.tickModulation(dt));
  }

  /** Detach the ticker only — modTracks are owned by mount/unmount. */
  private stopModulation(): void {
    try { this.modUnsub?.(); } catch { /* gone */ }
    this.modUnsub = null;
  }

  /** Push this frame's modulator + mood values into each tracked layer:
   *  opacity → the container (baseOpacity × modulator × mood × day-response),
   *  density/speed → the content handle's emitter setters (× day-response).
   *  Phase 2: the time-of-day day-response multipliers (godRays dim at night,
   *  fog thickens, weather gusts) fold in alongside the modulators + mood so
   *  `final = baseOpacity × modulator × mood × dayResponse` composes cleanly. */
  private tickModulation(dtMs: number): void {
    this.modElapsedMs += dtMs;
    // Per-level base mood × dynamic combat-heat pulse (CombatMoodDriver) ×
    // boss-phase swell (BossMoodDriver). Each is orthogonal — a multiplier
    // that intensifies the ambiance without overwriting the level's base.
    const mood = this.sceneMood * this.combatMood * this.bossMood;
    const t = this.modElapsedMs;
    const phase = this.todPhase;
    const knobs = this.dayKnobs;
    const hasDayResponse = phase !== null && knobs.length > 0;
    // Reactivity envelopes — one boolean guard keeps the no-pulse frame free
    // (a frame with no live pulses adds one integer check per track).
    const pulseNow = this.nowMs();
    const pulsing = this.reactivity.hasActive(pulseNow);
    for (const tr of this.modTracks) {
      // Day-response per-layer multipliers (1 when no time signal / no knob).
      const dayOp = hasDayResponse ? dayOpacityMultiplier(knobs, tr.kind, phase!) : 1;
      const daySpd = hasDayResponse ? daySpeedMultiplier(knobs, tr.kind, phase!) : 1;
      const dayDen = hasDayResponse ? dayDensityMultiplier(knobs, tr.kind, phase!) : 1;
      // Reactivity pulse factors (1 when nothing live) — one more multiplicative
      // channel in the same product as mood + day-response (spec §1.3 seam).
      const pOp = pulsing ? this.reactivity.factorFor(tr.kind, 'opacity', pulseNow) : 1;
      const pDen = pulsing ? this.reactivity.factorFor(tr.kind, 'density', pulseNow) : 1;
      const pSpd = pulsing ? this.reactivity.factorFor(tr.kind, 'speed', pulseNow) : 1;
      const pTint = pulsing ? this.reactivity.factorFor(tr.kind, 'tint', pulseNow) : 1;
      const pulseNeutral = pOp === 1 && pDen === 1 && pSpd === 1 && pTint === 1;
      // Neutral + unmodulated → skip churn. `pulseTouched` buys one restoring
      // write after a pulse decays out (else the last elevated value strands).
      if (mood === 1 && tr.mods.length === 0 && dayOp === 1 && daySpd === 1 && dayDen === 1
        && pulseNeutral && !tr.pulseTouched) continue;
      tr.pulseTouched = !pulseNeutral;
      if (tr.container) {
        const moodOp = tr.mooded ? mood : 1;
        tr.container.setAlpha(tr.baseOpacity * modulatedValue(tr.mods, 'opacity', t, tr.seed) * moodOp * dayOp * pOp);
      }
      if (tr.handle.setDensity) tr.handle.setDensity(modulatedValue(tr.mods, 'density', t, tr.seed) * mood * dayDen * pDen);
      if (tr.handle.setSpeed) tr.handle.setSpeed(modulatedValue(tr.mods, 'speed', t, tr.seed) * daySpd * pSpd);
      if (tr.handle.setTint) {
        const tint = modulatedTintValue(tr.mods, t, tr.seed);
        // Pulse tint envelopes lerp toward the layer's authored tintTo: the
        // factor's excursion above 1 maps to the 0..1 setTint parameter; the
        // stronger of modulator-tint vs pulse-tint wins the frame.
        const pulseT = Math.max(0, Math.min(1, pTint - 1));
        if (tint !== null || pulseT > 0) tr.handle.setTint(Math.max(tint ?? 0, pulseT));
      }
    }
  }

  /** Active preset id, or null when no atmosphere is mounted. */
  activePreset(): string | null {
    return this.currentPreset;
  }

  /** Test/debug — ambient sprite count (fireflies + god-rays + glow-blobs). */
  spriteCount(): number {
    return this.layers.reduce((n, l) => n + (l.handle.spriteCount?.() ?? 0), 0);
  }

  /** Test/debug — soft-light count in the live scene (lightField layers). */
  softLightCount(): number {
    return this.layers.reduce((n, l) => n + (l.handle.lightCount?.() ?? 0), 0);
  }

  /** Read a query param from the current URL (safe in node/ssr). */
  private readUrlParam(key: string): string | undefined {
    try {
      return bootSearchParams().get(key) ?? undefined;
    } catch {
      return undefined;
    }
  }
}

export const AtmosphereLayer = new AtmosphereLayerImpl();
